/**
 * reportsXlsxExport — convertit les tables rendues d'une sélection de rapports
 * (DOM offscreen de PrintReportDocument) en un classeur Excel propre :
 *   • 1 onglet par rapport (onglets supplémentaires si plusieurs tables) ;
 *   • en-têtes multi-niveaux aplatis en une seule ligne (aucune cellule fusionnée) ;
 *   • nombres formatés suisse (1'234, CHF, %) reparsés en vraies valeurs numériques ;
 *   • mise en forme légère (titre, en-tête, gel, largeurs, formats), sans formule.
 *
 * Lecture seule du DOM — n'altère pas le rendu print existant (pdf/html/pptx).
 */
import { p } from '../i18n/useP';

// Toutes les variantes d'apostrophe de milliers : droite + typographiques.
const APOS = /['‘’ʼ′´]/g;

// ── Parsing d'une cellule formatée suisse → valeur numérique + format Excel ──
function parseNumeric(raw) {
  if (raw == null) return null;
  // \s couvre tous les espaces (y c. NBSP/narrow). On retire CHF et apostrophes.
  let t = String(raw).replace(/CHF/gi, '').replace(/\s/g, '').replace(APOS, '');
  let pct = false;
  if (t.endsWith('%')) { pct = true; t = t.slice(0, -1); }
  t = t.replace(/[–—−]/g, '-').replace(/,/g, '.');
  let neg = false;
  if (/^\(.*\)$/.test(t)) { neg = true; t = t.slice(1, -1); }
  if (t === '' || t === '-' || !/^-?\d+(\.\d+)?$/.test(t)) return null;
  let n = parseFloat(t);
  if (neg) n = -n;
  if (pct) return { value: n / 100, numFmt: '0.0%' };
  // Séparateur de milliers = virgule (placeholder Excel) → rendu apostrophe
  // automatiquement en locale suisse. NE PAS mettre d'apostrophe littérale ici.
  return { value: n, numFmt: Number.isInteger(n) ? '#,##0' : '#,##0.00' };
}

// ── Table DOM → matrice pleine (colspan/rowspan dupliqués, pas de trous) ──
function buildMatrix(rows) {
  const m = [];
  rows.forEach((tr, ri) => {
    if (!m[ri]) m[ri] = [];
    let ci = 0;
    Array.from(tr.cells).forEach(cell => {
      while (m[ri][ci] !== undefined) ci++;
      const text = cell.textContent.replace(/\s+/g, ' ').trim();
      const rs = cell.rowSpan || 1, cs = cell.colSpan || 1;
      for (let r = 0; r < rs; r++)
        for (let c = 0; c < cs; c++) {
          if (!m[ri + r]) m[ri + r] = [];
          m[ri + r][ci + c] = text;
        }
      ci += cs;
    });
  });
  const w = m.reduce((mx, r) => Math.max(mx, r.length), 0);
  m.forEach(r => { for (let i = 0; i < w; i++) if (r[i] === undefined) r[i] = ''; });
  return m;
}

function headerRowCount(table) {
  if (table.tHead && table.tHead.rows.length) return table.tHead.rows.length;
  let n = 0;
  for (const tr of Array.from(table.rows)) {
    const cells = Array.from(tr.cells);
    if (cells.length && cells.every(c => c.tagName === 'TH')) n++;
    else break;
  }
  return n || 1;
}

function tableToRows(table) {
  const rows = Array.from(table.rows);
  if (!rows.length) return null;
  const m = buildMatrix(rows);
  const hc = Math.min(headerRowCount(table), m.length);
  const width = m[0].length;
  const header = [];
  for (let j = 0; j < width; j++) {
    const parts = [];
    for (let i = 0; i < hc; i++) {
      const v = m[i][j];
      if (v && parts[parts.length - 1] !== v) parts.push(v);
    }
    header.push(parts.join(' · '));
  }
  return { header, body: m.slice(hc) };
}

// ── Noms d'onglet : ≤31 car., caractères interdits retirés, uniques ──
function sanitizeSheetName(name) {
  return name.replace(/[\\/?*[\]:]/g, ' ').replace(/^'+|'+$/g, '').trim().slice(0, 31) || 'Sheet';
}
function uniqueSheetName(used, label, idx) {
  const base = sanitizeSheetName(idx ? `${label} (${idx})` : label);
  let name = base, k = 2;
  while (used.has(name.toLowerCase())) {
    const suffix = ` (${k++})`;
    name = sanitizeSheetName(base.slice(0, 31 - suffix.length) + suffix);
  }
  used.add(name.toLowerCase());
  return name;
}

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE7DD' } };
const HEADER_FONT = { bold: true, color: { argb: 'FF3A3027' } };
const TITLE_FONT = { bold: true, size: 12, color: { argb: 'FF3A3027' } };
const HEADER_BORDER = { bottom: { style: 'thin', color: { argb: 'FFB9A98F' } } };

export async function exportReportsToXlsx(rootEl, { tenantName, year } = {}) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Palestre';
  const used = new Set();

  const wrappers = Array.from(rootEl.querySelectorAll('[data-report-key]'));
  for (const w of wrappers) {
    const label = w.getAttribute('data-report-label') || 'Report';
    const tables = Array.from(w.querySelectorAll('table')).filter(t => t.rows.length);
    tables.forEach((table, idx) => {
      const parsed = tableToRows(table);
      if (!parsed || !parsed.header.length) return;
      const ws = wb.addWorksheet(uniqueSheetName(used, label, tables.length > 1 ? idx + 1 : 0));

      // Titre (cellule simple, pas de fusion).
      ws.getCell('A1').value = [label, tenantName, year && String(year)].filter(Boolean).join('  ·  ');
      ws.getCell('A1').font = TITLE_FONT;

      const headerRowIdx = 3;
      parsed.header.forEach((h, j) => {
        const cell = ws.getRow(headerRowIdx).getCell(j + 1);
        cell.value = h;
        cell.font = HEADER_FONT;
        cell.fill = HEADER_FILL;
        cell.border = HEADER_BORDER;
      });

      parsed.body.forEach((r, ri) => {
        const row = ws.getRow(headerRowIdx + 1 + ri);
        r.forEach((raw, j) => {
          const cell = row.getCell(j + 1);
          const num = parseNumeric(raw);
          if (num) {
            cell.value = num.value;
            cell.numFmt = num.numFmt;
            cell.alignment = { horizontal: 'right' };
          } else {
            cell.value = raw === '' ? null : raw;
          }
        });
      });

      ws.views = [{ state: 'frozen', ySplit: headerRowIdx }];

      const widths = [];
      const note = (j, len) => { widths[j] = Math.max(widths[j] || 0, len); };
      parsed.header.forEach((h, j) => note(j, String(h).length));
      parsed.body.forEach(r => r.forEach((c, j) => note(j, String(c).length)));
      widths.forEach((wd, j) => { ws.getColumn(j + 1).width = Math.min(Math.max(wd + 2, 8), 48); });
    });
  }

  if (wb.worksheets.length === 0) {
    wb.addWorksheet('Empty').getCell('A1').value = p('Aucune donnée tabulaire dans la sélection.');
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Palestre_${(tenantName || 'tenant')}_${year || ''}.xlsx`.replace(/\s+/g, '_').replace(/_+\.xlsx$/, '.xlsx');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
